import { useState } from 'react'

import {
  CategoryCard,
  Heading,
  Input,
  ImageWrap,
} from '@hhgtech/hhg-components/components'
import { NavMenuContentType } from '@hhgtech/hhg-components/components/navigationV2/index'
import { theme } from '@hhgtech/hhg-components/misc'
import { GetServerSideProps } from 'next'
import { useIntl } from 'react-intl'

import { checkMobile } from 'api/utils/validators'
import { Container } from 'components/atoms/container'
import { HealthToolBreadcrumb } from 'components/molecules/healthToolBreadcrumb'
import MobileBottomNavigationWrapper from 'components/organisms/mobileBottomNavigationWrapper'
import {
  StyledCategoriesContainer,
  StyledCategoryWrapper,
  StyledContentContainer,
  StyledCustomBackground,
  StyledSearchArea,
} from 'components/pages/categories/index.styled'
import {
  NavigatorLayout,
  getDefaultLanguageOptions,
} from 'components/templates/navigatorLayout'
import { useIsMobile } from 'hooks/useIsMobile'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { LOCALE_SPECS, assetImagePath, basePath } from 'interfaces/constants'
import { CategoriesLayout, Category, FooterInfo } from 'interfaces/types'
import { getFooterDataPersist } from 'utils/isomorphicCache/footerData'
import { getNavMenuContentPersist } from 'utils/isomorphicCache/navMenuContent'

type Props = {
  isMobileSsr: boolean
  footerInfo: FooterInfo
  dataNavMenuContent?: NavMenuContentType
}
const Categories = ({ footerInfo, dataNavMenuContent }: Props) => {
  const isMobile = useIsMobile()
  const { formatMessage: f } = useIntl()

  const {
    locale,
    router: { asPath },
  } = useRouterExtend()
  const siteName = LOCALE_SPECS[locale].SITE_NAME_FORMATTED
  const categoriesType = (LOCALE_SPECS[locale].CATEGORIES_TYPE ||
    'long') as CategoriesLayout

  const categories = (dataNavMenuContent?.categories?.items || []) as Category[]
  const [searchResultCategories, setSearchResultCategories] = useState<
    Category[]
  >(categories)

  const [searchValue, setSearchValue] = useState('')

  const onSearchChange = (v: string) => {
    setSearchValue(v)
    setSearchResultCategories(
      v && v !== ''
        ? categories.filter(
            (category) =>
              (category &&
                category.categoryName &&
                category.categoryName.toLowerCase().search(v.toLowerCase()) >
                  -1) ||
              (category.slug &&
                category.slug
                  .replace('-', '')
                  .toLowerCase()
                  .search(v.replace(' ', '').toLowerCase()) > -1),
          )
        : categories,
    )
  }

  return (
    <NavigatorLayout
      title={f({ id: 'categories.seo.title' }) + ' | ' + siteName}
      description={f(
        { id: 'categories.seo.description' },
        { sitename: siteName },
      )}
      image={`${assetImagePath}/bookDoctor.svg`}
      SEOData={{ isIndexable: true }}
      footerInfo={footerInfo}
      dataNavMenuContent={dataNavMenuContent}
      languageOptions={getDefaultLanguageOptions(locale, asPath)}
    >
      <>
        <HealthToolBreadcrumb
          pageItem={[
            {
              label: f({
                id: 'categories.breadcrumb.allCategories',
                defaultMessage: 'All Categories',
              }),
              value: `${basePath}/categories`,
            },
          ]}
        />
        <StyledSearchArea>
          <StyledCustomBackground />
          <Container>
            <StyledContentContainer>
              <Heading
                tag={isMobile ? 'h3' : 'h2'}
                color={theme.colors.secondaryBase}
                as="h1"
              >
                {f({
                  id: 'categories.start.title',
                  defaultMessage: "Don't know where to start?",
                })}
              </Heading>
              <Input
                style={{
                  minHeight: 'auto',
                  padding: '16px 0',
                  minWidth: isMobile ? '100%' : 'auto',
                }}
                value={searchValue}
                name="categories-search"
                size="md"
                placeholder={f({
                  id: 'categories.search.placeHolder',
                  defaultMessage: 'Search a category',
                })}
                onChange={onSearchChange}
                displayIcon={
                  <ImageWrap
                    src={`${assetImagePath}/search.svg`}
                    style={{ width: '24px', height: '24px' }}
                    alt={f({
                      id: 'categories.search.placeHolder',
                      defaultMessage: 'Search a category',
                    })}
                  />
                }
              />
            </StyledContentContainer>
          </Container>
          {isMobile ? (
            <StyledCategoriesContainer data-layout-type={categoriesType}>
              {searchResultCategories.map((category) => (
                <StyledCategoryWrapper
                  data-layout-type={categoriesType}
                  key={category.termId}
                >
                  <CategoryCard
                    category={category}
                    imgBanner={
                      <ImageWrap
                        src={
                          category.iconUrl || `${assetImagePath}/bookDoctor.svg`
                        }
                        style={{
                          width: '63px',
                          height: '63px',
                          objectFit: 'contain',
                        }}
                        alt={category.displayName}
                      />
                    }
                    label={category.displayName}
                  />
                </StyledCategoryWrapper>
              ))}

              <MobileBottomNavigationWrapper />
            </StyledCategoriesContainer>
          ) : (
            <Container>
              <StyledCategoriesContainer data-layout-type={categoriesType}>
                {searchResultCategories.map((category) => (
                  <StyledCategoryWrapper
                    data-layout-type={categoriesType}
                    key={category.termId}
                  >
                    <CategoryCard
                      href={category.permalink}
                      category={category}
                      imgBanner={
                        <ImageWrap
                          src={
                            category.iconUrl ||
                            `${assetImagePath}/bookDoctor.svg`
                          }
                          style={{
                            width: '63px',
                            height: '63px',
                            objectFit: 'contain',
                          }}
                          alt={category.displayName}
                        />
                      }
                      label={category.displayName}
                    />
                  </StyledCategoryWrapper>
                ))}
              </StyledCategoriesContainer>
            </Container>
          )}
        </StyledSearchArea>
      </>
    </NavigatorLayout>
  )
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { req, locale } = context
  const userAgent = `${req.headers['user-agent']}`

  const isMobile = checkMobile(userAgent)
  const results = await Promise.all([
    getFooterDataPersist(),
    getNavMenuContentPersist(locale),
  ])

  const [footerInfo, dataNavMenuContent] = results

  return {
    props: {
      isMobileSsr: isMobile,
      footerInfo: footerInfo?.data || null,
      dataNavMenuContent: dataNavMenuContent || {},
    },
  }
}

export default Categories
